/**
 * The sentence above the Biler result (#625 AC8): which filters the result
 * was built from. Built from the APPLIED search, never from the inputs — the
 * inputs may have been edited since the user pressed Søk.
 *
 * The words come from leads:bilerSummary (#714). Called during render, so the
 * sentence follows a language switch.
 */
import i18n from '../../i18n'

// No fixed language: each call reads the current one.
const t = i18n.getFixedT(null, 'leads')

/** Models named in the sentence before it shortens to "og N til". */
export const SUMMARY_MODELL_LIMIT = 3

export interface BilerSummaryInput {
  total: number
  totalIsCapped: boolean
  merkeNavn?: string | null
  modell?: string[]
  regFrom?: string // YYYY-MM
  regTo?: string // YYYY-MM
  eierskap?: 'eid' | 'leaset'
  vehicleClass?: string
}

/** "2024-03" → "mars 2024"; anything else is returned untouched. */
export function formatMonth(ym: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(ym)
  if (!m) return ym
  const months = t('bilerSummary.months', { returnObjects: true }) as string[]
  const idx = Number(m[2]) - 1
  return months[idx] ? t('bilerSummary.monthYear', { month: months[idx], year: m[1] }) : ym
}

function periodText(regFrom?: string, regTo?: string): string {
  if (regFrom && regTo) {
    return t('bilerSummary.periodBoth', { from: formatMonth(regFrom), to: formatMonth(regTo) })
  }
  if (regFrom) return t('bilerSummary.periodFrom', { from: formatMonth(regFrom) })
  if (regTo) return t('bilerSummary.periodTo', { to: formatMonth(regTo) })
  return t('bilerSummary.periodAll')
}

function modellText(modell?: string[]): string {
  if (!modell || modell.length === 0) return t('bilerSummary.modellAll')
  const shown = modell.slice(0, SUMMARY_MODELL_LIMIT).join(', ')
  const rest = modell.length - SUMMARY_MODELL_LIMIT
  if (modell.length === 1) return t('bilerSummary.modellOne', { n: modell.length, shown })
  return rest > 0
    ? t('bilerSummary.modellManyMore', { n: modell.length, shown, rest })
    : t('bilerSummary.modellMany', { n: modell.length, shown })
}

export function buildBilerSummary(s: BilerSummaryInput): string {
  const count = s.totalIsCapped ? '1000+' : s.total.toLocaleString('nb-NO')
  const orgs = !s.totalIsCapped && s.total === 1 ? t('bilerSummary.orgOne') : t('bilerSummary.orgMany')
  const merke = s.merkeNavn
    ? t('bilerSummary.merke', { merke: s.merkeNavn, modell: modellText(s.modell) })
    : t('bilerSummary.allMerker', { modell: modellText(s.modell) })
  const klasse = s.vehicleClass === 'personbil' ? t('bilerSummary.klassePersonbil')
    : s.vehicleClass === 'varebil' ? t('bilerSummary.klasseVarebil') : ''
  const eierskap = s.eierskap === 'eid' ? t('bilerSummary.eierskapEid')
    : s.eierskap === 'leaset' ? t('bilerSummary.eierskapLeaset') : t('bilerSummary.eierskapBoth')
  return t('bilerSummary.sentence', { count, orgs, merke, klasse, period: periodText(s.regFrom, s.regTo), eierskap })
}
